/**
 * Periodically frees lock rows whose lastHeartbeat is older than the stale
 * threshold. Any tab can run this loop; the reap itself is a CAS-style sweep.
 *
 * Timing: every reaperIntervalMs (default 3 s), threshold staleThresholdMs
 * (default 10 s) — see CLAUDE.md §4.4 + data/storage-schema.yaml#tabLocks.
 */
import type { TabLockRepository, TabLockRow } from '@/persistence/ports';
import type { TabBroadcast } from './broadcast';
import { DEFAULT_STALE_THRESHOLD_MS } from './CrossTabLock';

export const DEFAULT_REAPER_INTERVAL_MS = 3_000;

export interface LockReaperOptions {
  readonly reaperIntervalMs?: number;
  readonly staleThresholdMs?: number;
  readonly now?: () => number;
  readonly broadcast?: TabBroadcast;
  readonly onReap?: (rows: readonly TabLockRow[]) => void;
}

export class LockReaper {
  readonly #repo: TabLockRepository;
  readonly #intervalMs: number;
  readonly #thresholdMs: number;
  readonly #now: () => number;
  readonly #broadcast: TabBroadcast | undefined;
  readonly #onReap: ((rows: readonly TabLockRow[]) => void) | undefined;
  #handle: ReturnType<typeof setInterval> | null = null;

  constructor(repo: TabLockRepository, opts: LockReaperOptions = {}) {
    this.#repo = repo;
    this.#intervalMs = opts.reaperIntervalMs ?? DEFAULT_REAPER_INTERVAL_MS;
    this.#thresholdMs = opts.staleThresholdMs ?? DEFAULT_STALE_THRESHOLD_MS;
    this.#now = opts.now ?? (() => Date.now());
    this.#broadcast = opts.broadcast;
    this.#onReap = opts.onReap;
  }

  /** One reap pass. Returns the rows it removed. */
  async reapOnce(): Promise<readonly TabLockRow[]> {
    const cutoff = this.#now() - this.#thresholdMs;
    const reaped = await this.#repo.reapStale(cutoff);
    if (reaped.length > 0) {
      for (const r of reaped) {
        this.#broadcast?.post({
          type: 'LOCK_RELEASED',
          cpid: r.cpid,
          wsUrl: r.wsUrl,
          tabId: r.tabId,
        });
      }
      this.#onReap?.(reaped);
    }
    return reaped;
  }

  start(): void {
    if (this.#handle !== null) return;
    this.#handle = setInterval(() => {
      void this.reapOnce();
    }, this.#intervalMs);
  }

  stop(): void {
    if (this.#handle === null) return;
    clearInterval(this.#handle);
    this.#handle = null;
  }
}
